import { MainContent } from '../shared-mui-components/MainContent.tsx';
import { HeaderWithButtons } from '../shared-mui-components/HeaderWithButtons.tsx';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { BarChart, CloudDownload } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import {
    ChartSection,
    DataPointUnit,
    numberFormatter,
    OperatorBarChart,
    ScrollableContainer,
} from './OperatorBarChart.tsx';
import { FilterBar } from '../shared-mui-components/filter/FiltersBox.tsx';
import { usePermitStatisticsFilterConfiguration } from '../shared-mui-components/filter/OperatorFilterConfiguration.tsx';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { FiltersDropdown } from '../parkingabo-users/OperatorParkingaboUsersFilterList.tsx';
import {
    buildFilterPayload,
    setSearchURL,
} from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import { useLocation, useNavigate } from 'react-router';
import {
    ButtonWithDropdown,
    DropdownItem,
} from '../layout/components/ButtonWithDropdown.tsx';
import { Fragment, useState } from 'react';
import {
    RequestStatus,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { openNewTab } from 'dg-web-shared/common/utils/Navigation.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { EmptyResults } from '../shared-mui-components/ListMessages.tsx';
import Alert from '@mui/material/Alert';
import { useReadOnlyExportContext } from '../app/components/ReadOnlyExportContext.tsx';
import {
    ConfirmReadOnlyExport,
    StatisticNotReadyTooltip,
} from './OperatorCheckinStatisticsRoute.tsx';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';

export interface PermitStatisticsResponse {
    data: PermitStatisticYear[];
    maxMonthTotalRevenue: number;
    maxMonthTotalCount: number;
}

interface PermitStatisticYear {
    year: number;
    totalRevenue: number;
    totalCount: number;
    months: PermitStatisticMonth[];
}

interface PermitStatisticMonth {
    yearMonth: string;
    countTotal: number;
    revenueTotal: number;
    countParkingpay: number;
    revenueParkingpay: number;
    countTwint: number;
    revenueTwint: number;
    countParkingportalCounter: number;
    revenueParkingportalCounter: number;
    countQuickcheckout: number;
    revenueQuickcheckout: number;
}

enum AggregateDuration {
    MONTHLY = 'MONTHLY',
    DAILY = 'DAILY',
}

export function OperatorPermitStatisticsRoute() {
    const { operatorExternalId } = useOperatorContext();
    const {
        filterConfig,
        activeFilters,
        listState,
        filterConfigState,
        searchText,
    } = usePermitStatisticsFilterConfiguration();
    const { language } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const readOnlyTransactions = useReadOnlyExportContext();
    const tooltipNames = [
        'Parkingpay',
        'TWINT',
        'Parkingportal',
        'Quick-Checkout',
    ];

    const [singleTransagtionModalOpen, setSingleTransagtionModalOpen] =
        useState(false);
    const [permitsExportState, requestPermitsExport] = useServerWrite<
        string,
        null
    >(queryString => ({
        url: `/ui-api/operator-account/statistics/permits/export/single-permits?${queryString}&language=${language}`,
    }));

    useServerSuccessEffect(
        permitsExportState,
        readOnlyTransactions.refetchReadOnlyExportData,
    );

    const hasRunningExport = readOnlyTransactions.runningExportId !== null;
    const notReady = listState.status !== RequestStatus.SUCCESS;
    const exportDropdownItems: DropdownItem[] = [
        {
            key: 'lock',
            label: (
                <Localized
                    de="Gruppiert nach Tag"
                    fr="Groupée par jour"
                    it="Raggruppata per giorno"
                    en="Grouped by day"
                />
            ),
            disabled: notReady,
            onClickCallback: () => {
                openNewTab(
                    urlWithOperatorExternalId(
                        `/ui-api/operator-account/statistics/permits/export/xlsx?lang=${language}&aggregateDuration=${
                            AggregateDuration.DAILY
                        }&${buildFilterPayload(activeFilters, searchText)}`,
                        operatorExternalId,
                    ),
                );
            },
            tooltip: notReady ? <StatisticNotReadyTooltip /> : undefined,
        },
        {
            key: 'lock',
            label: (
                <Localized
                    de="Gruppiert nach Monat"
                    fr="Groupée par mois"
                    it="Raggruppata per mese"
                    en="Grouped by month"
                />
            ),
            disabled: notReady,
            onClickCallback: () => {
                openNewTab(
                    urlWithOperatorExternalId(
                        `/ui-api/operator-account/statistics/permits/export/xlsx?lang=${language}&aggregateDuration=${
                            AggregateDuration.MONTHLY
                        }&${buildFilterPayload(activeFilters, searchText)}`,
                        operatorExternalId,
                    ),
                );
            },
            tooltip: notReady ? <StatisticNotReadyTooltip /> : undefined,
        },
        {
            key: 'single_transactions',
            label: (
                <Localized
                    de="Einzeln"
                    fr="Individuellement"
                    it="Singolarmente"
                    en="Individually"
                />
            ),
            tooltip: hasRunningExport ? (
                <Localized
                    de="Ein export läuft bereits."
                    fr="Une exportation est déjà en cours."
                    it="È già in corso un'esportazione."
                    en="An export is already running."
                />
            ) : notReady ? (
                <StatisticNotReadyTooltip />
            ) : (
                <Localized
                    de="Es werden Bewilligungen ab dem 01.01.2026 exportiert."
                    fr="Les autorisations à partir du 01/01/2026 seront exportées."
                    it="Le autorizzazioni a partire dal 01/01/2026 verranno esportate."
                    en="Permits from 01/01/2026 will be exported."
                />
            ),
            disabled:
                permitsExportState.status === RequestStatus.PENDING ||
                hasRunningExport ||
                notReady,
            onClickCallback: () => setSingleTransagtionModalOpen(true),
        },
    ];

    return (
        <MainContent>
            <HeaderWithButtons
                title={
                    <Localized
                        de={`Statistiken ${EmDash} Bewilligungen`}
                        fr={`Statistiques ${EmDash} Autorisations`}
                        it={`Statistiche ${EmDash} Autorizzazioni`}
                        en={`Statistics ${EmDash} Permits`}
                    />
                }
                icon={BarChart}
                headerButtons={
                    <ButtonWithDropdown
                        label={
                            <Localized
                                de="Exportieren"
                                fr="Exporter"
                                it="Esporta"
                                en="Export"
                            />
                        }
                        itemVariant={'action'}
                        variant="outlined"
                        startIcon={<CloudDownload />}
                        dropdownItems={exportDropdownItems}
                    />
                }
            />
            <FilterBar>
                <FiltersDropdown
                    activeFilters={activeFilters}
                    filterConfig={filterConfig}
                    onFiltersStateChange={activeFilters =>
                        setSearchURL(
                            navigate,
                            location,
                            searchText,
                            activeFilters,
                        )
                    }
                    pending={
                        filterConfigState.status === RequestStatus.PENDING &&
                        Object.keys(filterConfig).length === 0
                    }
                />
            </FilterBar>
            <ScrollableContainer>
                <Alert severity="info" sx={{ mx: 4 }}>
                    <Localized
                        de="Die Werte der Statistiken werden anhand des Ausstellungsdaums der Bewilligung berechnet."
                        fr="Les valeurs des statistiques sont calculées sur la base de la date d'émission de l'autorisation."
                        it="I valori delle statistiche vengono calcolati sulla base della data di emissione dell'autorizzazione."
                        en="The values in the statistics are calculated based on the date of issue of the permit."
                    />
                </Alert>
                <OperatorAsyncLoadedSection
                    requestState={listState}
                    render={data => {
                        if (data.data.length === 0) {
                            return (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        height: '100%',
                                    }}
                                >
                                    <EmptyResults />
                                </Box>
                            );
                        }

                        return (
                            <>
                                {data.data.map(yearData => (
                                    <Fragment key={yearData.year}>
                                        <Typography
                                            component="div"
                                            variant="h6"
                                            sx={{
                                                backgroundColor: theme =>
                                                    theme.palette.primary.main,
                                                color: theme =>
                                                    theme.palette.primary
                                                        .contrastText,
                                                fontWeight: theme =>
                                                    theme.typography
                                                        .fontWeightBold,
                                                textAlign: 'center',
                                                marginX: 4,
                                                marginTop: 2,
                                            }}
                                        >
                                            {yearData.year}
                                        </Typography>
                                        <Stack
                                            direction="row"
                                            spacing={4}
                                            sx={{
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                margin: 4,
                                                marginTop: 2,
                                                marginBottom: 8,
                                            }}
                                        >
                                            <ChartSection
                                                title={
                                                    <>
                                                        <Localized
                                                            de="Anzahl "
                                                            fr="Nombre "
                                                            it="Numero "
                                                            en="Number "
                                                        />
                                                    </>
                                                }
                                                caption={
                                                    <>
                                                        (
                                                        {numberFormatter(
                                                            yearData.totalCount,
                                                            DataPointUnit.PCS,
                                                            false,
                                                        )}
                                                        )
                                                    </>
                                                }
                                            >
                                                <OperatorBarChart
                                                    data={yearData.months}
                                                    maxYValue={
                                                        data.maxMonthTotalCount
                                                    }
                                                    totalKey="countTotal"
                                                    dataKeys={[
                                                        'countParkingpay',
                                                        'countTwint',
                                                        'countParkingportalCounter',
                                                        'countQuickcheckout',
                                                    ]}
                                                    tooltipNames={tooltipNames}
                                                    yearMonthKey="yearMonth"
                                                />
                                            </ChartSection>
                                            <ChartSection
                                                title={
                                                    <>
                                                        <Localized
                                                            de="Volumen "
                                                            fr="Volume "
                                                            it="Volume "
                                                            en="Volume "
                                                        />
                                                    </>
                                                }
                                                caption={
                                                    <>
                                                        (
                                                        {numberFormatter(
                                                            yearData.totalRevenue,
                                                            DataPointUnit.CHF,
                                                            true,
                                                        )}
                                                        )
                                                    </>
                                                }
                                            >
                                                <OperatorBarChart
                                                    data={yearData.months}
                                                    maxYValue={
                                                        data.maxMonthTotalRevenue
                                                    }
                                                    totalKey="revenueTotal"
                                                    dataKeys={[
                                                        'revenueParkingpay',
                                                        'revenueTwint',
                                                        'revenueParkingportalCounter',
                                                        'revenueQuickcheckout',
                                                    ]}
                                                    tooltipNames={tooltipNames}
                                                    yearMonthKey="yearMonth"
                                                    unit={DataPointUnit.CHF}
                                                />
                                            </ChartSection>
                                        </Stack>
                                    </Fragment>
                                ))}
                            </>
                        );
                    }}
                />
                <ConfirmReadOnlyExport
                    open={singleTransagtionModalOpen}
                    onConfirm={() => {
                        requestPermitsExport(
                            buildFilterPayload(activeFilters, searchText),
                        );
                        setSingleTransagtionModalOpen(false);
                    }}
                    onCancel={() => setSingleTransagtionModalOpen(false)}
                />
            </ScrollableContainer>
        </MainContent>
    );
}
